import { z } from 'zod';
import { MoneyWireSchema } from './money.js';
import { WaiverElectionSchema } from './waiver.js';
/**
 * Deposit collection + waiver election contract (E-SF05 AC2) — the shapes for
 * `POST /bff/deposit/intent`, `/bff/deposit/confirm`, and
 * `/bff/deposit/waiver-election`.
 *
 * ── SAQ-A: NO CARD DATA CROSSES OUR BOUNDARY (ADR-0009) ─────────────────────
 * The deposit is a Stripe **card** charge under SAQ-A. Card data (PAN, CVC) is
 * tokenised by Stripe **in the browser** and never touches the BFF or any Rheora
 * service. The BFF creates a Stripe *PaymentIntent* server-side (with the Stripe
 * SECRET key it alone holds) and returns to the browser only:
 *   - the Stripe **publishable** key (public by design, like a Turnstile site key), and
 *   - the PaymentIntent **client token** (Stripe's `client_secret`, which is
 *     browser-facing BY DESIGN — Elements needs it to confirm the card).
 *
 * ⚠️ FIELD-NAME NOTE. The client token is renamed from Stripe's `client_secret`
 * to `clientToken` DELIBERATELY: the BFF's boot-time response guard forbids any
 * response key matching `/secret/` (the lint anchor that keeps real service
 * secrets off the wire). Stripe's client_secret is public-by-design, so per the
 * guard's own "rename it" guidance we carry it under a name that does not trip
 * the anchor. The VALUE is unchanged; the FE hands it to `stripe.confirmPayment`.
 *
 * ── v0.1 DEGRADES TO A BADGED STUB (ADR-0009) ──────────────────────────────
 * When no Stripe test-mode keys are configured, the intent returns `mode:'stub'`
 * with a visible badge ("Deposit (stub — Stripe pending)"). The stub collects no
 * card data and moves no money — it is the honest, ADR-blessed v0.1 state, and
 * it keeps SAQ-A trivially (there is no card surface at all).
 */
/**
 * The funnel's deposit sub-state, held server-side in the funnel session (the
 * BFF has no DB; there is no Accounts account pre-sign). AC2's "abandonment
 * leaves the account in DEPOSIT_PENDING funnel state" is realised HERE: the
 * credit "deposit" branch sets `DEPOSIT_PENDING`, and it stays there until the
 * customer collects the deposit or records a waiver. E-SF06 (sign) reads this
 * gate and refuses to proceed while it is `DEPOSIT_PENDING`.
 */
export const FunnelDepositState = z.enum([
    'DEPOSIT_PENDING',
    'DEPOSIT_COLLECTED',
    'DEPOSIT_WAIVED',
    'DEPOSIT_INSTALLMENT_SCHEDULED',
]);
// ── POST /bff/deposit/intent ────────────────────────────────────────────────
export const DepositIntentRequestSchema = z.object({
    /** The opaque premise handle; the BFF redeems it to the pending deposit. */
    candidateId: z.string().min(1),
});
/**
 * Intent response — a discriminated union on `mode` so the SPA renders either the
 * real Stripe Elements card field or the badged stub, and cannot confuse them.
 */
export const DepositIntentResponseSchema = z.discriminatedUnion('mode', [
    z.object({
        mode: z.literal('stripe_test'),
        /** Stripe PUBLISHABLE key — public by design; belongs in the browser. */
        publishableKey: z.string().min(1),
        /** Stripe PaymentIntent client token (its `client_secret`; browser-facing). */
        clientToken: z.string().min(1),
        /** Stripe PaymentIntent id (`pi_…`) — not a secret; used to confirm. */
        paymentIntentId: z.string().min(1),
        amount: MoneyWireSchema,
    }),
    z.object({
        mode: z.literal('stub'),
        /** Visible badge text the FE must render (ADR-0009 v0.1). */
        badge: z.string().min(1),
        amount: MoneyWireSchema,
    }),
]);
// ── POST /bff/deposit/confirm ───────────────────────────────────────────────
export const DepositConfirmRequestSchema = z.object({
    candidateId: z.string().min(1),
    /**
     * The Stripe PaymentIntent id (`pi_…`) the browser confirmed. NOT a secret and
     * NOT card data — just the id, which the BFF re-checks server-side against
     * Stripe before advancing the funnel (never trusts the browser's word for it).
     */
    paymentIntentId: z.string().min(1),
});
/**
 * The MASKED card block — the ONLY card data that may cross any boundary
 * (ADR-0009 SAQ-A). It is derived server-side from the Stripe PaymentMethod on a
 * settled deposit and carries strictly:
 *   - `brand` / `last4` / `expMonth` / `expYear` — the display-safe descriptor
 *     PCI-DSS permits showing (brand + last four + expiry), NEVER the PAN or CVC;
 *   - `paymentMethodRef` — the Stripe `pm_…` token (an opaque reference, not card
 *     data);
 *   - `status` — the deposit funnel state at the moment the card settled.
 *
 * ⚠️ There is DELIBERATELY no `pan`/`cardNumber`/`cvc`/`cvv` field, and the
 * boot-time response guard (`http/response-guard.ts`) forbids ever adding one:
 * the full number + CVC are tokenised in the browser by Stripe and never reach
 * this origin. This schema is the shared source of truth reused by the confirm
 * response AND the BFF→Accounts accept payload so the two can never drift.
 */
export const DepositPaymentMethodSchema = z.object({
    /** Card network (e.g. `visa`, `mastercard`). Display-safe. */
    brand: z.string().min(1),
    /** The last four PAN digits — the ONLY digits PCI-DSS permits displaying. */
    last4: z.string().regex(/^\d{4}$/u),
    /** Expiry month 1–12. Not card-secret data. */
    expMonth: z.number().int().min(1).max(12),
    /** Four-digit expiry year. Not card-secret data. */
    expYear: z.number().int().min(2000).max(2100),
    /** Stripe PaymentMethod id (`pm_…`) — an opaque token, never card data. */
    paymentMethodRef: z.string().min(1),
    /** Deposit funnel state when the card settled (mirrors `FunnelDepositState`). */
    status: FunnelDepositState,
});
export const DepositConfirmResponseSchema = z.object({
    funnelState: FunnelDepositState,
    /**
     * The masked card descriptor — present only when the gateway settled a REAL
     * Stripe card (the browser tokenised it). Absent for the synthetic/stub
     * gateways (honest empty state — never a fabricated card).
     */
    paymentMethod: DepositPaymentMethodSchema.optional(),
});
// ── POST /bff/deposit/card-on-file ──────────────────────────────────────────
//
// The NO-STRIPE masked "card on file" path (dev/demo capture). Unlike the Stripe
// intent/confirm flow above, the browser DERIVES the display-safe descriptor
// (brand/last4/expMonth/expYear) from the typed card LOCALLY and DISCARDS the full
// number + CVV — only those four masked fields ever cross the wire, and nothing is
// tokenised by Stripe. The gateway is NOT involved (this works when
// DEPOSIT_GATEWAY_MODE=stub). The BFF assigns a synthetic `paymentMethodRef`
// (`manual:<last4>`, never a Stripe `pm_…` token and never a PAN) and the E-SF06
// sign saga forwards the masked descriptor onto the account at accept.
//
// ⚠️ SAME SAQ-A BOUNDARY, ENFORCED STRUCTURALLY. `.strict()` means a
// `pan`/`cardNumber`/`cvv`/`cvc` field is REJECTED (not silently dropped): the full
// number + CVV must never reach this origin. NOTE: deriving `last4` in browser JS
// briefly touches the PAN, so this is a DEV/DEMO capture — real production must use
// Stripe Elements (the intent/confirm flow above) to keep the PAN out of scope.
export const MaskedCardInputSchema = z
    .object({
    /** Card network (e.g. `visa`). Display-safe; derived in the browser. */
    brand: z.string().min(1),
    /** Last four PAN digits — the ONLY digits PCI-DSS permits displaying. */
    last4: z.string().regex(/^\d{4}$/u),
    expMonth: z.number().int().min(1).max(12),
    expYear: z.number().int().min(2000).max(2100),
})
    .strict();
export const CardOnFileRequestSchema = z
    .object({
    candidateId: z.string().min(1),
    /** MASKED descriptor only — `.strict()` above forbids any PAN/CVV field. */
    card: MaskedCardInputSchema,
})
    .strict();
export const CardOnFileResponseSchema = z.object({
    funnelState: FunnelDepositState,
    /** The masked card now on file (same shape the sign saga forwards to Accounts). */
    paymentMethod: DepositPaymentMethodSchema,
});
// ── POST /bff/deposit/waiver-election ───────────────────────────────────────
export const WaiverElectionRequestSchema = z.object({
    candidateId: z.string().min(1),
    election: WaiverElectionSchema,
});
/**
 * The resulting funnel state after recording the election: `DEPOSIT_WAIVED` for a
 * full-waiver ground, `DEPOSIT_INSTALLMENT_SCHEDULED` for the low-income split.
 * The response carries no PII and no certification reference back.
 */
export const WaiverElectionResponseSchema = z.object({
    status: z.literal('recorded'),
    funnelState: FunnelDepositState,
});
//# sourceMappingURL=deposit.js.map